import { H5, H6 } from "@sellar-io/components";
import {
  FulfillmentTypeListResponseType,
  StockListResponseType,
  SupplierGetResponseType,
} from "@sellar-io/api-v2/types";
import {
  findProductFulfillmentTypeDeliveryCharge,
  formatCurrency,
  isIneligibleForFulfillmentType,
} from "@sellar-io/common";
import { ReactElement, ReactNode, useState } from "react";
import { useSearchParams } from "react-router";
import { StockIndicator } from "../../../../components";
import { QuantityIndicator } from "../../../../components/quantityIndicator/QuantityIndicator";
import {
  RadioGroup,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../../../components/radioSelector/RadioGroup";
import { ContextOrderBasketType, useSellarUser } from "../../../../hooks";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";

import { DiscountedPrice } from "../../DiscountedPrice";
import CategoryPageDietaryTypes from "../CategoryPageDietaryTypes";
import { AvailableFromBadge } from "../../../../components/badge/AvailableFromBadge";
import {
  SkeletonContainer,
  SkeletonText,
} from "../../../../components/skeleton/Skeleton";
import { CheckoutSelectedFulfillmentType } from "../..";

export default function CategoryListElementMobile({
  category,
  fulfillmentTypeContainerDeliveryCharges,
  supplier,
  children,
  onClick,
  order,
  selectedFulfillmentType,
  courierFulfillmentType,
}: {
  category: StockListResponseType[number];
  fulfillmentTypeContainerDeliveryCharges?: {
    volume: number;
    packQuantity: number;
    displayUnit: string;
    containerType: string;
    charge: number;
  }[];
  supplier: SupplierGetResponseType | undefined;
  children?: ReactNode;
  onClick?: (productId: number) => void;
  order?: ContextOrderBasketType | null;
  selectedFulfillmentType?: CheckoutSelectedFulfillmentType;
  courierFulfillmentType: FulfillmentTypeListResponseType[number] | undefined;
}): ReactElement {
  const { user } = useSellarUser();
  const { business } = useSellarCurrentBusiness();
  const isLoggedIn = !!business && !!user;
  const [searchParams] = useSearchParams();
  const storefrontLink =
    searchParams.get("storefrontLink") || searchParams.get("priceTierLink");
  const [selectedProductId, setSelectedProductId] = useState<number>();
  const selectedProduct = category.Products.find(
    (product) => product.id === selectedProductId,
  );

  const palletOnly =
    isIneligibleForFulfillmentType({
      item: {
        containerType: selectedProduct?.containerType || null,
        unit: selectedProduct?.unit || null,
        volume: selectedProduct?.volume || null,
      },
      fulfillmentType: courierFulfillmentType,
    }) && selectedFulfillmentType === "courier";

  const getDeliveryCharge = (
    product: StockListResponseType[number]["Products"][number],
  ) => {
    return (
      findProductFulfillmentTypeDeliveryCharge({
        fulfillmentTypeContainerDeliveryCharges,
        product: {
          ...product,
          displayUnit: product.unit,
        },
      })?.charge ?? 0
    );
  };

  const getBasketItemQuantity = (productId: number) => {
    return (
      order?.OrderItems?.find((item) => item.Product.id === productId)
        ?.quantity ?? 0
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <RadioGroup
        value={selectedProductId?.toString()}
        onChange={(value) => {
          const productId = parseInt(value);
          setSelectedProductId(productId);
          onClick?.(productId);
        }}
      >
        <RadioGroupContainer>
          {category.Products.map((child) => (
            <>
              <RadioGroupOption value={child.id.toString()}>
                <div className="grid w-full grid-cols-12 items-center gap-2">
                  <H6 className="col-span-6">
                    {" "}
                    {child.name?.split(" • ").slice(1).join(" • ")}{" "}
                    <CategoryPageDietaryTypes child={child} parent={category} />
                    {!!child.availableFrom && (
                      <AvailableFromBadge availableFrom={child.availableFrom} />
                    )}
                  </H6>
                  <H5 className="col-span-4">
                    {(isLoggedIn || storefrontLink) &&
                    child.ProductPriceTier &&
                    child.ProductPriceTier.price >= 0 ? (
                      <DiscountedPrice
                        productPriceTier={child.ProductPriceTier}
                      />
                    ) : (
                      <SkeletonContainer className={"flex w-full items-center"}>
                        <SkeletonText className="h-6 w-full bg-zinc-100" />
                      </SkeletonContainer>
                    )}
                  </H5>
                  <div className="">
                    <StockIndicator
                      stock={child.availableStock}
                      type={"minimal"}
                      canBackorder={child.canBackorder}
                    />
                  </div>
                  <div className="">
                    {getBasketItemQuantity(child.id) > 0 && order && (
                      <>
                        {
                          <QuantityIndicator
                            quantity={getBasketItemQuantity(child.id)}
                          />
                        }
                      </>
                    )}
                  </div>
                </div>
              </RadioGroupOption>
            </>
          ))}
        </RadioGroupContainer>
      </RadioGroup>
      <div className="flex flex-col items-center gap-4">
        {(isLoggedIn || storefrontLink) &&
          selectedProduct &&
          !!getDeliveryCharge(selectedProduct) && (
            <H6>
              {" "}
              Item delivery charge:{" "}
              {palletOnly
                ? "Pallet only"
                : formatCurrency(
                    getDeliveryCharge(selectedProduct),
                    supplier?.currency,
                  )}
            </H6>
          )}
        {children}
        <H6>Price per unit and excludes VAT.</H6>
      </div>
    </div>
  );
}
